import { DateTime } from "luxon"
import { StrictOmit } from "../../../../lib/lang"
import { MonetaryAmount } from "../../../currency/currencyTypes"
import { ProductGroupOption } from "./components/FormikProductGroupField"
import { Shop } from "../shops/shopsTypes"
import { DeliveryLocation } from "../deliveryLocations/deliveryLocationsTypes"
import { ProductUserDetails } from "../priceList/priceListTypes"
import { ProductGroupCode } from "../productGroup/productGroupTypes"

export type BaseProduct = {
    name: string
    price: MonetaryAmount
    isActive: boolean
}

export type ProductsTableItem = Readonly<
    BaseProduct & {
        id: number
        marketingDetails?: {
            isOffer?: boolean
        }
        productPhoto?: {
            uid?: string
            url: string
        }
        userDetails?: ProductUserDetails
        offerDetails?: OfferDetailsType
        productDescription?: {
            marketingTag: string
            allergens: Allergen[]
        }
    }
>

export type ProductShop = StrictOmit<Shop, "isActive" | "uid">

export type Product = BaseProduct & {
    id: number
    merchantId: number
    productNumber: string
    description: string
    productPhoto?: {
        uid?: string
        url: string
    }
    productGroup: {
        id: number
        name: string
    }
    shops?: ProductShop[]
    productDescription?: {
        marketingTag: string
        allergens: Allergen[]
    }
    availabilityDetails?: AvailabilityDetails
    deadline?: Deadline
    offerDetails?: OfferDetailsType
    priceLists?: PriceList[]
    barcode?: string
    paymentDetails?: {
        isPriceHidden: boolean
    }
    deliveryDetails?: {
        location: DeliveryLocation
    }
    itemLimits?: {
        maxDailyItemsPerPerson: number
        minItemsPerOrder: number
    }
}

export type CreateProduct = Readonly<
    StrictOmit<BaseProduct, "isActive"> & {
        description?: string
        isActive?: boolean
        productNumber?: string
        productPhoto?: {
            uid?: string | null
            url?: string
        }
        productGroup: Record<"id", number>
        shops: Record<"id", number>[]
        productDescription?: {
            marketingTag: string
            allergens: {
                id: number
            }[]
        }
        availabilityDetails?: AvailabilityDetails
        deadline?: Deadline
        offerDetails?: OfferDetailsType
        priceLists?: { id: number; adjustedPrice: MonetaryAmount }[]
        barcode?: string
        paymentDetails?: {
            isPriceHidden: boolean
        }
        deliveryDetails?: {
            location: { id: number }
        }
        itemLimits?: {
            maxDailyItemsPerPerson: number
            minItemsPerOrder: number
        }
    }
>

export type AvailabilityDetails = Readonly<{
    dateInterval?: {
        from?: string
        to?: string
    }
    allowPreOrder: boolean
}>

export type Deadline = Readonly<{
    purchases: {
        daysBefore: number
        timeOfDay: string
    }
    cancellation: {
        daysBefore: number
        timeOfDay: string
    }
}>

export type OfferDetailsType = Readonly<{
    discount: {
        type: DiscountType
        percentage?: {
            amount: number
            scale: number
        }
        price?: MonetaryAmount
        displayLabelType?: DisplayLabelType
    }
    productsOnSale?: number
    availabilityDetails: {
        dateTimeInterval?: {
            from?: string
            to?: string
        }
    }
}>

export enum DiscountType {
    NONE = "NONE",
    DISCOUNT_PRICE = "DISCOUNT_PRICE",
    DISCOUNT_PERCENTAGE = "DISCOUNT_PERCENTAGE",
}

export type OfferDetailsFormValues = Readonly<{
    discount: {
        type: DiscountType
        percentage?: {
            amount: string
        }
        price?: number
        displayLabelType?: DisplayLabelType
    }
    productsOnSale?: number
    availabilityDetails: {
        dateTimeInterval?: {
            from?: DateTime
            to?: DateTime
        }
    }
}>

export type PriceList = Readonly<{
    id: number
    name: string
    adjustedPrice: MonetaryAmount
}>

export type ProductGroups = Readonly<{
    id: number
    name: string
    products: ProductsTableItem[]
}>

export type GroupItem = Readonly<
    {
        type: "group"
        id: number
        name: string
    } & Record<Exclude<keyof ProductsTableItem, "name" | "id">, undefined>
>

export type ProductItem = Readonly<
    {
        type: "product"
        groupId?: number
    } & ProductsTableItem
>

export type ProductTableRow = GroupItem | ProductItem

export type CreateUpdateProductFormValues = Readonly<{
    productGroup: ProductGroupOption | null
    productGroupCode?: ProductGroupCode
    productNumber: string
    name: string
    price: number | undefined
    currency: string
    shops: ProductShop[]
    description: string
    isActive?: boolean
    productPhoto?: {
        uid?: string
        url: string
    }
    productDescription?: {
        marketingTag: string
        allergens: Allergen[]
    }
    availabilityDetails: {
        dateInterval?: {
            from?: DateTime | null
            to?: DateTime | null
        }
        allowPreOrder: boolean
    }
    deadline?: {
        purchases: {
            daysBefore: number
            timeOfDay: DateTime
        }
        cancellation: {
            daysBefore: number
            timeOfDay: DateTime
        }
    } | null
    offerDetails?: OfferDetailsType
    priceLists?: PriceList[]
    barcode?: string
    paymentDetails?: {
        isPriceHidden: boolean
    }
    deliveryDetails?: {
        location: { id: number }
    }
    itemLimits?: {
        maxDailyItemsPerPerson: number
        minItemsPerOrder: number
    } | null
}>

export type Allergen = Readonly<{
    id: number
    name: string
}>

export enum DisplayLabelType {
    LIMITED_ON_SALE = "LIMITED_ON_SALE",
    SAVE_X = "SAVE_X",
    GREAT_OFFER = "GREAT_OFFER",
    BUY_NOW = "BUY_NOW",
    DAY_OFFER = "DAY_OFFER",
    WEEK_OFFER = "WEEK_OFFER",
    DISCOUNT = "DISCOUNT",
    GOOD_PRICE = "GOOD_PRICE",
    OLD_NEW_PRICE = "OLD_NEW_PRICE",
}
